// View individual mesa QR code

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../config/firebase/config';
import { useRestaurant } from '../../../providers/RestaurantContext';
import QRCode from 'qrcode';
import { ArrowLeft, Download, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';

interface MesaData {
  id: string;
  numeroMesa: string;
  estado: string;
  restaurantId?: string;
  posOrderId?: string | null;
}

const MesaQRCodeScreen: React.FC = () => {
  const { id: mesaId } = useParams();
  const navigate = useNavigate();
  const { restaurant } = useRestaurant();
  const [mesaData, setMesaData] = useState<MesaData | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(true);

  // Load mesa; when session-driven, use active session for estado and posOrderId
  useEffect(() => {
    if (!mesaId || !restaurant?.id) return;

    const mesaRef = doc(db, 'restaurants', restaurant.id, 'mesas', mesaId);
    let unsubSession: (() => void) | null = null;

    const unsubscribe = onSnapshot(mesaRef, (snapshot) => {
      if (!snapshot.exists()) return;

      const data = snapshot.data();
      const activeSessionId = data.activeSessionId;
      const base = {
        id: snapshot.id,
        numeroMesa: data.numeroMesa || '',
        estado: data.estado || 'cerrada',
        restaurantId: restaurant.id,
        posOrderId: data.posOrderId ?? null,
      };

      if (typeof activeSessionId !== 'string' || !activeSessionId) {
        unsubSession?.();
        unsubSession = null;
        setMesaData(base);
        return;
      }

      unsubSession?.();
      const sessionRef = doc(mesaRef, 'sessions', activeSessionId);
      unsubSession = onSnapshot(sessionRef, (sessionSnap) => {
        if (!sessionSnap.exists()) {
          setMesaData(base);
          return;
        }
        const s = sessionSnap.data();
        setMesaData({
          ...base,
          estado: (s.estado === 'abierta' || s.estado === 'cerrada' ? s.estado : base.estado),
          posOrderId: s.posTicketId ?? base.posOrderId,
        });
      });
    });

    return () => {
      unsubscribe();
      unsubSession?.();
    };
  }, [mesaId, restaurant?.id]);

  // Generate QR code: /mesa/{mesaId} only (backend discovers POS ticket by mesa; no ?ticket= to avoid broken QRs when ticket changes)
  useEffect(() => {
    if (!mesaId) return;

    const generateQR = async () => {
      setIsGenerating(true);
      try {
        const baseUrl = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_BASE_URL || '');
        const mesaUrl = `${baseUrl}/mesa/${mesaId}`;

        const dataUrl = await QRCode.toDataURL(mesaUrl, {
          width: 400,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrDataUrl(dataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
        toast.error('Error al generar código QR');
      } finally {
        setIsGenerating(false);
      }
    };

    generateQR();
  }, [mesaId]);

  const handleDownloadQR = () => {
    if (!qrDataUrl || !mesaData) {
      toast.error('QR code no disponible');
      return;
    }
    
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `mesa-${mesaData.numeroMesa}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`QR de Mesa ${mesaData.numeroMesa} descargado`);
  };

  const handleGoBack = () => {
    navigate('/admin/mesas');
  };

  if (!mesaData) {
    return (
      <div className="min-h-screen w-full flex flex-col px-6 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-zinc-400" />
            <p className="text-zinc-500">Cargando datos de la mesa...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col px-6 py-6 animate-fade-in">
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-8 w-full">
        {/* Title */}
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">Mesa #{mesaData.numeroMesa}</h1>
          <p className="text-zinc-400 text-base mt-2 font-medium">Código QR de acceso</p>
        </div>

        {/* Back Button - Top Right */}
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 px-6 py-3 bg-zinc-900/40 backdrop-blur-xl border border-white/5 text-white rounded-full hover:bg-zinc-800/60 hover:border-yap-primary/30 transition-all font-semibold shadow-lg hover:shadow-[0_0_20px_-5px_rgba(0,255,132,0.15)]"
        >
          <ArrowLeft size={18} />
          Volver atrás
        </button>
      </div>

      {/* Main Content - Centered */}
      <div className="flex flex-col items-center justify-center flex-1 max-w-2xl mx-auto w-full">
        {/* QR Code Container */}
        <div className="bg-zinc-900/40 backdrop-blur-xl rounded-2xl border border-white/5 p-8 transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30 w-full">
          {/* Status Badge */}
          <div className="flex justify-center mb-6">
            <span className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
              mesaData.estado === 'abierta' 
                ? 'bg-yap-primary/20 text-yap-primary border border-yap-primary/30' 
                : 'bg-zinc-800/60 text-zinc-400 border border-white/5'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                mesaData.estado === 'abierta' ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              {mesaData.estado === 'abierta' ? 'Ocupada' : 'Cerrada'}
            </span>
          </div>

          {/* QR Code Display */}
          <div className="flex flex-col items-center mb-6">
            {isGenerating ? (
              <div className="w-80 h-80 flex items-center justify-center bg-zinc-800/30 rounded-2xl border-2 border-dashed border-white/10">
                <div className="text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-zinc-400" />
                  <div className="text-sm text-zinc-500 font-medium">Generando código QR...</div>
                </div>
              </div>
            ) : qrDataUrl ? (
              <div className="bg-white p-6 rounded-2xl shadow-2xl">
                <img 
                  src={qrDataUrl} 
                  alt={`QR Code para Mesa ${mesaData.numeroMesa}`}
                  className="w-80 h-80"
                />
              </div>
            ) : (
              <div className="w-80 h-80 flex items-center justify-center bg-zinc-800/30 rounded-2xl border-2 border-dashed border-white/10">
                <div className="text-sm text-zinc-500 font-medium">Error al generar QR</div>
              </div>
            )}
          </div>

          {/* Mesa URL */}
          <div className="mb-6 text-center">
            <p className="text-xs text-zinc-500 font-medium mb-1">Enlace directo:</p>
            <p className="text-sm text-zinc-400 font-mono break-all bg-zinc-800/30 px-4 py-2 rounded-lg">
              {typeof window !== 'undefined' ? `${window.location.origin}/mesa/${mesaId}` : `/mesa/${mesaId}`}
            </p>
          </div>

          {/* Download Button */}
          <div className="flex justify-center">
            <button
              onClick={handleDownloadQR}
              disabled={!qrDataUrl}
              className="flex items-center gap-2 px-6 py-3 bg-yap-primary text-black rounded-full hover:bg-yap-hover disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm transition-all shadow-[0_0_20px_-5px_rgba(0,255,132,0.3)] hover:scale-105 active:scale-95"
            >
              <Download className="w-5 h-5" />
              Descargar Código QR
            </button>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 text-center">
          <p className="text-zinc-500 text-sm font-medium">
            Escanea este código para acceder directamente a la mesa desde cualquier dispositivo
          </p>
        </div>
      </div>
    </div>
  );
};

export default MesaQRCodeScreen;
